if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('[id^="GalleryDots"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.initDots();
        this.initSwipe();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initDots() {
        const dotsContainer = this.elements.dots;
        if (!dotsContainer || !this.elements.viewer) return;

        const slides = this.elements.viewer.querySelectorAll('[id^="Slide-"]');
        this.dotElements = [];

        slides.forEach((slide, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product-media-dot';
          dot.setAttribute('aria-label', `Image ${index + 1}`);
          dot.addEventListener('click', () => this.goToSlideIndex(index));
          dotsContainer.appendChild(dot);
          this.dotElements.push(dot);
        });

        if (this.dotElements.length <= 1) {
          dotsContainer.style.display = 'none';
          return;
        }

        if (this.dotElements.length > 0) {
          this.dotElements[0].classList.add('is-active');
        }
      }

      updateDots(activeIndex) {
        if (!this.dotElements) return;
        this.dotElements.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === activeIndex);
        });
      }

      initSwipe() {
        if (!this.elements.viewer) return;
        const slider = this.elements.viewer.slider || this.elements.viewer.querySelector('[id^="Slider-"]');
        if (!slider) return;

        let startX = 0;
        let startY = 0;
        let isDragging = false;

        slider.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          isDragging = true;
        }, { passive: true });

        slider.addEventListener('touchmove', (e) => {
          if (!isDragging) return;
          const deltaX = Math.abs(e.touches[0].clientX - startX);
          const deltaY = Math.abs(e.touches[0].clientY - startY);
          if (deltaX > deltaY && deltaX > 10) {
            e.preventDefault();
          } else {
            isDragging = false;
          }
        }, { passive: false });

        slider.addEventListener('touchend', (e) => {
          if (!isDragging) return;
          isDragging = false;
          const deltaX = startX - e.changedTouches[0].clientX;
          if (Math.abs(deltaX) < 50) return;

          const currentIndex = this.getCurrentIndex();
          if (deltaX > 0) {
            this.goToSlideIndex(currentIndex + 1);
          } else {
            this.goToSlideIndex(currentIndex - 1);
          }
        }, { passive: true });
      }

      getCurrentIndex() {
        const viewer = this.elements.viewer;
        const slider = viewer.slider || viewer.querySelector('[id^="Slider-"]');
        if (!slider) return 0;
        const offset = viewer.sliderItemOffset || slider.clientWidth || 1;
        return Math.round(slider.scrollLeft / offset);
      }

      goToSlideIndex(index) {
        const viewer = this.elements.viewer;
        const slides = viewer.sliderItemsToShow || Array.from(viewer.querySelectorAll('[id^="Slide-"]'));
        if (!slides || !slides.length) return;
        const n = slides.length;
        const safeIndex = ((index % n) + n) % n;
        const targetSlide = slides[safeIndex];
        if (!targetSlide) return;
        const mediaId = targetSlide.dataset.mediaId;
        if (mediaId) {
          this.setActiveMedia(mediaId, false);
        }
        this.updateDots(safeIndex);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateDots((event.detail.currentPage || 1) - 1);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
